import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const accent = { fontFamily: "'Playfair Display', serif", fontStyle: "italic" } as const;
const mono = { fontFamily: "'JetBrains Mono', monospace" } as const;

const VideoGratuiteMerci = () => {
  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-17857202947/-iolCPbEh94bEIOW_cJC",
        value: 1.0,
        currency: "CAD",
      });
    }
  }, []);

  return (
    <>
      <SEOHead
        title="Merci — Vidéo gratuite"
        description="Merci pour votre demande. On vous contacte bientôt pour planifier votre tournage."
        canonical="/videogratuite/merci"
        noIndex
      />

      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <header className="border-b border-border">
          <div className="max-w-[1280px] mx-auto px-6 md:px-8 h-16 flex items-center">
            <Logo variant="full" size="md" />
          </div>
        </header>

        <main className="flex-1 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
          </div>

          <section className="relative max-w-[640px] mx-auto px-6 md:px-8 py-24 md:py-32 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-primary/10 border border-primary/20 mb-8"
            >
              <CheckCircle className="w-10 h-10 md:w-12 md:h-12 text-primary" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl md:text-7xl leading-[1.05] tracking-tight mb-6"
            >
              <span className="text-primary" style={accent}>Merci!</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-xl text-foreground/90 leading-relaxed"
            >
              On vous contacte bientôt afin de planifier votre vidéo gratuit 📹
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-16 border border-border rounded-xl p-8 md:p-10 bg-card text-left"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-4" style={mono}>
                En attendant
              </p>
              <h2 className="text-2xl md:text-3xl leading-tight mb-4">
                <span className="text-foreground">Va voir ce qu'on a déjà tourné pour </span>
                <span className="text-primary" style={accent}>nos partenaires.</span>
              </h2>
              <p className="text-base text-foreground/80 leading-relaxed mb-6">
                Jette un œil à nos dernières réalisations pour avoir une idée du résultat.
              </p>
              <Button asChild variant="hero" size="lg" className="w-full md:w-auto">
                <Link to="/portfolio">
                  Voir nos dernières réalisations
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </motion.div>
          </section>
        </main>

        <footer className="border-t border-border">
          <div
            className="max-w-[1280px] mx-auto px-6 md:px-8 py-8 text-center text-xs text-muted-foreground"
            style={mono}
          >
            © {new Date().getFullYear()} Agence 8020
          </div>
        </footer>
      </div>
    </>
  );
};

export default VideoGratuiteMerci;
